import "./itemCardListView.scss";

import PropTypes from "prop-types";
import PubSub from "pubsub-js";
import React, { PureComponent } from "react";
import { findDOMNode } from "react-dom";
import ImmutablePropTypes from "react-immutable-proptypes";

import { RELATIONSHIP_ROW_Y_PADDING } from "../../constants";
import { ITEM_CARD_LIST_VIEW } from "../../constants/identifiers";
import { METRICS_PREFIX } from "../../constants/itemFields";
import PubSubTopics from "../../constants/pubSubTopics";
import { isDateValid } from "../../modules/definition/calendar/dateFunctions";
import { immutableRecords, PropTypeShapes } from "../../types";
import { selectByPubSubTopics } from "../../types/immutableRecords/model/appState/utils";
import { relationshipDropPositionWithNesting } from "../../utils/dropFunctions";
import { updateSelectedItemsUsingUpdaters } from "../../utils/selection";
import { withContainerContext } from "../container/ContainerContext";
import { asDropTarget } from "../dragAndDrop/DroppableComponent";
import { withScreenContext } from "../screen/ScreenContext";
import { withYScrollPositionContext } from "../scrollable/ScrollPositionContext";
import withScrollableView from "../scrollable/withScrollableView";
import ItemCardRowView from "./ItemCardRowView";

// import debugUtils from "../../utils/debugUtils";

const dropCallbacks = {
  onEnter: (props, dragItem, _dropTarget) => {
    if (props.onDragEnter) {
      props.onDragEnter(dragItem.multipleItemIds);
    }
  },

  onLeave: (props, _dragItem, dropTarget) => {
    dropTarget.component.onDragLeave();
  },

  getDragMultipleItemIds(props, dragItem, _dropTarget) {
    return props.selectedItemIds.contains(dragItem.itemId)
      ? props.sortedItemIds.filter(id => props.selectedItemIds.contains(id)).toArray()
      : [dragItem.itemId];
  },

  getDragStartParams(_props, _dragItem, _positions, _dropTarget) {
    return {};
  },

  onHover: (props, dragItem, positions, dropTarget) => {
    return dropTarget.component.onHover(props, dragItem, positions, dropTarget);
  },

  onDrop: (props, dragItem, positions, dropTarget) => {
    return dropTarget.component.onDrop(props, dragItem, positions, dropTarget);
  },

  getDropPreviewForItem: (_props, __dragItem, _dropTarget) => {
    return null; //dropTarget.component.getDropPreviewForItem(props, dragItem, dropTarget);
  }
};

// Convert to nested ones according to sequence/narrative
const adjustTextSizesProps = (obj, isNarrativeView) => {
  let props = Object.assign({}, obj);
  const prefix = isNarrativeView ? METRICS_PREFIX.NARRATIVE : METRICS_PREFIX.SEQUENCE;
  const suffix = "Metrics";
  props.titleMetrics = obj[`${prefix}Title${suffix}`];
  props.summaryMetrics = obj[`${prefix}Summary${suffix}`];
  return props;
};

class ItemCardListView extends PureComponent {
  constructor(props, context) {
    super(props, context);

    this.state = {
      dropRow: null,
      dropAction: null
    };

    this.onLiveRowDrag = this.onLiveRowDrag.bind(this);
    this.onDragEnter = this.onDragEnter.bind(this);
    this.onDragLeave = this.onDragLeave.bind(this);
    this.onRowDrop = this.onRowDrop.bind(this);

    this.onScrollToItemReceived = this.onScrollToItemReceived.bind(this);
    this.onScrollToDateReceived = this.onScrollToDateReceived.bind(this);
    this.onSelectItemByTopicsReceived = this.onSelectItemByTopicsReceived.bind(this);

    this.onItemClick = this.onItemClick.bind(this);
  }

  componentDidMount() {
    this._node = findDOMNode(this);
    this._pubSubScrollToItemToken = PubSub.subscribe(PubSubTopics.scrollToItem, this.onScrollToItemReceived);
    this._pubSubScrollToDateToken = PubSub.subscribe(PubSubTopics.scrollToDate, this.onScrollToDateReceived);
    this._pubSubSelectItemByTopicsToken = PubSub.subscribe(
      PubSubTopics.selectItemByTopics,
      this.onSelectItemByTopicsReceived
    );
    this.props.registerDropTarget(this, dropCallbacks);
  }

  componentWillReceiveProps(nextProps) {
    // debugUtils.logPropChanges("ItemCardListView", this.props, nextProps, true);
    if (nextProps.sortedItemIds !== this.props.sortedItemIds) {
      this._lastClickItemId = null;
      this._firstShiftClickSelection = null;
    }
  }

  componentWillUnmount() {
    PubSub.unsubscribe(this._pubSubScrollToItemToken);
    PubSub.unsubscribe(this._pubSubScrollToDateToken);
    PubSub.unsubscribe(this._pubSubSelectItemByTopicsToken);
  }

  onSelectItemByTopicsReceived(msg, selectTopic) {
    const newSelectedIds = selectByPubSubTopics(
      this.props.isActiveView,
      this.props.data.rows.map(row => row.get("itemDisplay")),
      selectTopic,
      this.props.selectedItemIds
    );
    if (newSelectedIds.length > 0) {
      this.props.onSelectItems(newSelectedIds);
      this.onScrollToItemReceived("", { id: newSelectedIds[0] });
    }
  }

  onScrollToItemReceived(msg, data) {
    this.scrollToItem(data.id);
  }

  scrollToItem(itemId) {
    const row = this.props.data.rows.find(row => row.itemDisplay.id === itemId);
    if (row) {
      this.props.yScrollActions.animateToViewport(null, row.rect.top);
    }
  }

  onScrollToDateReceived(_msg, date) {
    // Scroll to the first row on or after the specified date
    if (date) {
      const row = this.props.data.rows.find(row =>
        isDateValid(row.itemDisplay.startDate) ? row.itemDisplay.startDate.timestamp >= date.timestamp : false
      );
      if (row) {
        this.scrollToItem(row.itemDisplay.id);
        return;
      }
      // If all items are before the specified date, scroll to the last row with a date
      const lastDatedRow = this.props.data.rows.findLast(row => isDateValid(row.itemDisplay.startDate));
      if (lastDatedRow) {
        this.scrollToItem(lastDatedRow.itemDisplay.id);
      }
    }
  }

  onLiveRowDrag(itemIds, dropRow, dropAction) {
    if (!this.props.allowRowDragNesting) {
      this.props.onLiveRowDrag(itemIds, dropRow, dropAction);
    } else {
      this.setState({
        dropRow,
        dropAction
      });
    }
  }

  onDragEnter(_itemIds) {
    // this.props.onStartRowDrag(itemIds);
  }

  onDragLeave() {
    if (!this.props.allowRowDragNesting) {
      this.props.onDragLeave();
    } else {
      this.setState({
        dropRow: null,
        dropAction: null
      });
    }
  }

  onRowDrop(dragItem, dropRow, dropAction) {
    // Send the drop info to owner
    this.props.onRowDrop(dragItem, dropRow, dropAction);

    if (this.props.allowRowDragNesting) {
      // And if necessary, clear our drawn drop lines...
      this.setState({
        dropRow: null,
        dropAction: null
      });
    }
  }

  onHover(props, dragItem, positions, _dropTarget) {
    const dropRows = this.props.data.rows;
    const mouseY = positions.targetPointerPos.y;
    const result = relationshipDropPositionWithNesting(
      dropRows,
      mouseY,
      dragItem,
      this.props.canItemDropOnRow,
      RELATIONSHIP_ROW_Y_PADDING
    );
    this._dropRow = result.dropRow;
    this._dropAction = result.dropAction;

    // And notify whatever is listening to the table that this shit is going down...
    this.onLiveRowDrag ? this.onLiveRowDrag(dragItem.multipleItemIds, this._dropRow, this._dropAction) : null;
  }

  onDrop(_props, dragItem, _positions, _dropTarget) {
    this.onRowDrop(dragItem, this._dropRow, this._dropAction);
    this._dropRow = null;
    this._dropAction = null;
  }

  getVisibleRows(rows) {
    const scrollTop = this.props.yScrollPosition.viewport.min;
    const scrollBottom = this.props.yScrollPosition.viewport.max;
    const visible = rows.filter(e => {
      return e.getIn(["rect", "bottom"]) > scrollTop && e.getIn(["rect", "top"]) < scrollBottom;
    });
    return visible;
  }

  onItemClick(itemId) {
    updateSelectedItemsUsingUpdaters(
      this.props.screenContext.isEditMode,
      itemId,
      this.props.selectedItemIds,
      this.props.onRemoveFromSelection,
      this.props.onAddToSelection,
      this.props.onSelectItems
    );
  }

  render() {
    const { data } = this.props;

    const visible = this.getVisibleRows(data.rows);

    return (
      <div onClick={this.props.onViewClick} className={`cardListCanvasView`}>
        {visible.map(e => {
          const obj = e.itemDisplay.toObject();
          const isDragging = this.props.draggedRowIds && this.props.draggedRowIds.contains(obj.id);
          // Note: the rect width will be the maximum width of all items.
          // From the getting table date in the selector.
          // So we pass in the individual item card width for each item here.
          const rect = e.rect.merge({
            top: e.rect.top,
            bottom: e.rect.bottom,
            width: e.itemDisplay.itemCardSize.width,
            right: e.rect.left + e.itemDisplay.itemCardSize.width
          });
          return (
            <ItemCardRowView
              viewId={this.props.viewId}
              key={obj.id}
              rect={rect}
              {...adjustTextSizesProps(obj, this.props.isNarrativeView)}
              fontSize={this.props.fontSize}
              hasChildren={e.hasChildren}
              childrenExpanded={e.childrenExpanded}
              indentationLevel={e.indentationLevel}
              itemCardViewSettings={this.props.itemCardViewSettings}
              onClick={this.onItemClick}
              onToggleChildren={this.props.onToggleChildren}
              canDrag={this.props.allowRowDrag}
              drawDropAction={
                this.state.dropRow && this.state.dropRow.itemDisplay.id === e.itemDisplay.id
                  ? this.state.dropAction
                  : null
              }
              isDraggingRow={isDragging}
              isEditMode={this.props.isEditMode}
              typesById={this.props.typesById}
            />
          );
        })}
      </div>
    );
  }
}

ItemCardListView.propTypes = {
  viewId: PropTypes.string.isRequired,
  isActiveView: PropTypes.bool.isRequired,
  fontSize: PropTypes.number.isRequired,
  data: PropTypeShapes.itemCardListData.isRequired,
  sortedItemIds: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  selectedItemIds: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  onSelectItems: PropTypes.func.isRequired,
  onAddToSelection: PropTypes.func.isRequired,
  onRemoveFromSelection: PropTypes.func.isRequired,
  onViewClick: PropTypes.func.isRequired,
  onToggleChildren: PropTypes.func.isRequired,
  itemCardViewSettings: PropTypeShapes.itemCardViewSettings.isRequired,
  canTypeHaveSuperType: PropTypes.func.isRequired,

  isNarrativeView: PropTypes.bool.isRequired,

  allowRowDrag: PropTypes.bool,
  allowRowDragNesting: PropTypes.bool,
  draggedRowIds: ImmutablePropTypes.list,
  canItemDropOnRow: PropTypes.func.isRequired,
  isEditMode: PropTypes.bool,

  typesById: ImmutablePropTypes.map.isRequired,

  // Dragging rows
  onDragLeave: PropTypes.func,
  onStartRowDrag: PropTypes.func,
  onLiveRowDrag: PropTypes.func,
  onRowDrop: PropTypes.func,

  // From asDropTarget
  registerDropTarget: PropTypes.func.isRequired,

  // From withScrollPositionContext
  yScrollPosition: PropTypes.instanceOf(immutableRecords.ScrollPositionRecord).isRequired,
  yScrollActions: PropTypeShapes.scrollActions.isRequired,

  // From containerContext
  containerRect: PropTypeShapes.flatRect.isRequired,

  //#!if iOS === true || harvard == true
  // From withScreenContext
  screenContext: PropTypes.object
  //#!endif
};

const scrollableViewOptions = {
  scrollHorizontally: false,
  scrollVertically: true,
  showVerticalScrollbar: false
};

export default withScreenContext(
  withScrollableView(
    withContainerContext(
      withYScrollPositionContext(asDropTarget(ItemCardListView, ITEM_CARD_LIST_VIEW)),
      scrollableViewOptions
    )
  )
);
